<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="'Tab' + tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'Latest Uploads'"
          @tabClick="(index: number) => tabChange(index)"
          @close="(index: number) => closeTab(index)"
        >
          {{ tab.name }}
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="'keepAlive' + index"
            v-if="index === 0"
          >
            <BatchList
              ref="batchListRef"
              :key="'batchList' + tab.id"
              v-show="selectedTab === index"
              @create="createNewUpload"
            />
          </keep-alive>
          <keep-alive
            :key="'keepAlive' + tab.id"
            v-else
          >
            <BatchMetadataForm
              :key="'batchForm' + tab.id"
              v-show="selectedTab === index"
              @cancel="closeFormTab"
              @success="handleUploadSuccess"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
  </section>
</template>

<script lang="ts" setup>
  import { nextTick, Ref, ref } from 'vue';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import BatchList from '@/app/features/admin/batch/components/BatchList.vue';
  import BatchMetadataForm from '@/app/features/admin/batch/components/BatchMetadataForm.vue';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';

  interface TabHeader {
    id: string | number;
    name: string;
  }

  const alertStore = useAlertStore();
  const batchListRef: Ref<any> = ref(null);
  const selectedTab = ref(0);
  const tabs = ref<TabHeader[]>([
    { id: 'latest-uploads', name: 'Latest Uploads' },
  ]);

  const tabChange = (index: number) => {
    selectedTab.value = index;
    if (selectedTab.value === 0) {
      nextTick(() => {
        batchListRef.value?.[0]?.refresh();
      });
    }
  };

  const closeTab = (index: number) => {
    if (index === 0) return; // Don't close the main list tab

    tabs.value.splice(index, 1);

    // Select previous tab
    if (index <= selectedTab.value) {
      selectedTab.value = Math.max(0, selectedTab.value - 1);
      if (selectedTab.value === 0) {
        nextTick(() => {
          batchListRef.value?.[0]?.refresh();
        });
      }
    }
  };

  const createNewUpload = () => {
    // Check if form tab already exists
    const formTabIndex = tabs.value.findIndex((tab) => tab.id === 'new-upload');

    if (formTabIndex === -1) {
      // Add new form tab
      tabs.value.push({
        id: 'new-upload',
        name: 'New Upload',
      });
      selectedTab.value = tabs.value.length - 1;
    } else {
      // Switch to existing form tab
      selectedTab.value = formTabIndex;
    }
  };

  const closeFormTab = () => {
    const formTabIndex = tabs.value.findIndex((tab) => tab.id === 'new-upload');
    if (formTabIndex !== -1) {
      closeTab(formTabIndex);
    }
  };

  const handleUploadSuccess = () => {
    closeFormTab();
    alertStore.showNotification(
      'Batch metadata uploaded successfully',
      'Batch Upload',
      Motif.SUCCESS
    );
    // Refresh the list
    nextTick(() => {
      batchListRef.value?.[0]?.refresh();
    });
  };
</script>
